import { useLocation } from 'react-router-dom';

export function getFormattedTime(totalSeconds) {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const formattedHours = hours > 0 ? String(hours).padStart(2, '0') + ':' : '';
  const formattedMinutes = String(minutes).padStart(2, '0');
  const formattedSeconds = String(seconds).padStart(2, '0');

  return `${formattedHours}${formattedMinutes}:${formattedSeconds}`;
}

export const getHighestActiveGame = (games) => {
  const activeGames = games.filter(game => game.is_active);
  
  if (activeGames.length > 0) {
    const highestActiveGame = activeGames.reduce((highestGame, currentGame) => {
      return currentGame.id > highestGame.id ? currentGame : highestGame;
    }, activeGames[0]);

    return highestActiveGame;
  } else {
    return null;
  }
}

export const shuffleArray = (array) => {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
}

export const sortAndSelectTop = (array, sortOrder, isRandomOrder) => {
  let sortedArray = [...array].sort((a, b) => 
    sortOrder === 'asc' ? a.score - b.score : b.score - a.score
  );

  return isRandomOrder ? shuffleArray(sortedArray) : sortedArray;
}

export const getFormattedCurrency = (amount) => {
  return amount.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0
  })
}

export const fadeOutSound = (audio, duration) => {
  const stepTime = 50;
  const steps = duration / stepTime;
  const volumeStep = audio.volume / steps;

  const fadeInterval = setInterval(() => {
    if (audio.volume > volumeStep) {
      audio.volume -= volumeStep;
    } else {
      clearInterval(fadeInterval);
      audio.pause();
      audio.volume = 1;
    }
  }, stepTime);
};

export const calculateTimeInSeconds = (timeElapsed) => {
  if (!timeElapsed) return 0;
  const daysAndTimeComponents = timeElapsed.split(" ");
  let days = 0;
  let timeString = timeElapsed;

  if (daysAndTimeComponents.length > 1) {
    days = parseInt(daysAndTimeComponents[0], 10);
    timeString = daysAndTimeComponents[1];
  }

  const timeComponents = timeString.split(":");
  const hours = +timeComponents[0];
  const minutes = +timeComponents[1];
  const seconds = timeComponents[2] ? +timeComponents[2] : 0;

  return (days * 86400) + (hours * 3600) + (minutes * 60) + seconds;
}

export const getPathSegments = (path) => path.split('/').filter(Boolean);


const TITLE_CASE_SMALL_WORDS = new Set([
  'a', 'an', 'and', 'as', 'at', 'but', 'by', 'for', 'from', 'in',
  'nor', 'of', 'on', 'or', 'the', 'to', 'with',
]);

export const formatScnearioLevelToFilterName = (name) => {
  return name
    .replace(/-/g, ' ')
    .split(' ')
    .map((word, index) => {
      if (!word) return word;
      if (index > 0 && TITLE_CASE_SMALL_WORDS.has(word.toLowerCase())) {
        return word.toLowerCase();
      }
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
}

// Strip a trailing parenthesized suffix from a display name (e.g. for
// scenario level names where the backend tags the variant in parens:
// "Netflix (Corporate)" → "Netflix"). Only affects display — the
// underlying value is preserved by the caller.
export const stripParentheses = (name) => {
  if (!name) return name;
  return name.replace(/\s*\([^)]*\)\s*$/, '').trim();
}

// Map the course-level businesstype value to the entity_type tag used
// on scenario levels (BE / Principles) and account_items (Classification
// / Normal). Returns null when the course hasn't been onboarded yet so
// callers can default-permissive (show everything).
export const getTargetEntityType = (businessType) => {
  if (businessType === 'Corporation') return 'corporate';
  if (businessType === 'Noncorporate') return 'noncorporate';
  return null;
};

// Predicate: does the scenario level / account_item's entity_type
// "match" the instructor's selected business type?
//
//   target null/undefined     → permissive, show everything
//   entity null/undefined/''  → permissive, treat as shared
//   entity === 'both'/'all'   → shared, show in both flows
//   entity is an array        → matches if it contains target
//                               (handles records tagged for both
//                               corporate and noncorporate)
//   otherwise                 → strict equality with target
export const matchesEntityType = (entity, target) => {
  if (!target) return true;
  if (entity == null || entity === '') return true;
  if (Array.isArray(entity)) return entity.includes(target);
  if (entity === 'both' || entity === 'all') return true;
  return entity === target;
};


export const useIsInstructorContainingPage = () => {
  const location = useLocation();
  return location.pathname.startsWith('/instructor');
}

export const parseDescription = (rawDescription = '') => {
  const parts = rawDescription ? rawDescription.split(':').map(part => part.trim()) : [];
  const subtitle = parts.shift();
  const description = parts.join("<br><br>");
  
  return { subtitle, description };
};